import React, { useState, useEffect } from 'react';
import './style.css';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  Table,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TableCell,
  TableBody,
  TableSortLabel,
  TextField,
  useMediaQuery,
  ClickAwayListener,
  Tooltip,
  Popover,
  Checkbox,
  InputAdornment,
  Zoom,
  SvgIcon,
  CircularProgress,
  Tabs,
} from '@mui/material';
import {
  ChevronUp as KeyboardArrowUpIcon,
  Pen,
  AlertTriangle,
  Info,
  CheckCircle,
  ArrowRight,
  Copy,
  CheckCircle2
} from 'lucide-react';
import useAuth from 'src/hooks/useAuth';
import { makeStyles } from 'tss-react/mui';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';
import GetAllUsersAssociatedWithAGroup from 'src/api/GetAllUsersAssociatedWithAGroup';
import ApprovalRequest from 'src/api/ApprovalRequest';
import usePendingRequests from 'src/hooks/usePendingRequests';
import CircleHelp from 'src/assets/icons/CircleHelp';
import UpdateApprovalThreshold from 'src/api/UpdateApprovalThreshold';
import GetPendingRequests from 'src/api/GetPendingRequests';
import NoRequests from './NoRequests';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TableSortControls } from 'src/components/TableSortControls';
import { btcInfoWebsite } from 'src/config';

dayjs.extend(relativeTime);

const useStyles = makeStyles()(theme => ({
  table: {
    minWidth: 650
  },
  metalTable: {
    minWidth: 650,
    backgroundColor: 'Var(--neutral-2)'
  },
  userTextField: {
    minWidth: 276
  },
  metalListBox: {
    background: 'var(--neutral-2)'
  },
  timeRange: {
    backgroundColor: 'var(--neutral-2)',
    border: '1px solid var(--color-border)'
  },
  redesignBtn: {
    boxShadow: 'none',
    color: 'white',
    '&:hover, &.Mui-focusVisible': {
      background: 'rgba(255, 255, 255, 0.08)'
    }
  },
  chip: {
    '&:hover': {
      cursor: "pointer"
    }
  },
  noMaxWidth: {
    maxWidth: 'none',
  },
  mobileLabel: {
    paddingRight: 0,
    paddingLeft: 0
  },
  mobileValue: {
    paddingRight: 0,
    paddingLeft: 0,
    maxWidth: 100
  }
}));

const ApprovalsTable = ({
  requestsList,
  groupApprovalThreshold,
  groupName,
  groupId,
  approvalsLoading
} = props) => {
  const { t } = useTranslation();
  const { loggedInUser, getAccessToken } = useAuth();
  const theme = useTheme();
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const [approvals, setApprovals] = useState([]);
  const [openRows, setOpenRows] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('expiresAt');
  const [groupOwnersCount, setGroupOwnersCount] = useState({});
  const [editApproverThreshold, setEditApproverThreshold] = useState(false);
  const [approverThreshold, setApproverThreshold] = useState(
    groupApprovalThreshold
  );
  const [displayApproverList, setDisplayApproverList] = useState(null);
  const [
    pendingApprovalThresholdUpdate,
    setPendingApprovalThresholdUpdate
  ] = useState(null);
  const [ loading, setLoading ] = useState(false)
  const [usersOrderMobile, setUsersOrderMobile] = useState('asc')
  const [usersOrderByMobile, setUsersOrderByMobile] = useState('expiresAt')

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElCard, setAnchorElCard] = useState(null);
  const [openedPopoverId, setOpenedPopoverId] = useState(null);
  const [showAddressCopiedIcon, setShowAddressCopiedIcon] = useState(false);
  const { handlePendingRequestsUpdate, handlePendingRequestsCountUpdate, pendingRequestsCount } = usePendingRequests();

  const handleClick = (event, popoverId) => {
    setAnchorEl(event.currentTarget);
    setOpenedPopoverId(popoverId)
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenedPopoverId(null)
  };

  const handleCardClick = (event, popoverId) => {
    setAnchorElCard(event.currentTarget);
    setDisplayApproverList(popoverId)
  };

  const handleCardClose = () => {
    setAnchorElCard(null);
    setDisplayApproverList(null)
  };

  const handleMobileUsersCardsSort = (tableColumn, columnOrder) => {
    setUsersOrderMobile(columnOrder)
    setUsersOrderByMobile(tableColumn)
  }

  useEffect(() => {
    setLoading(true)
    let temp = requestsList?.map(approval => {
      if (approval.requestType === 'Update Approval Threshold'){
        setPendingApprovalThresholdUpdate(approval);
      }
      let t = {};
      const approved = approval?.status?.match(/\((\d+)/)[1]; // add number for how many have already approved
      const outstandingApproval = approval?.status?.match(/\/(\d+)\)/)[1]; // add number for outstanding approvals
      if (approval.subAccountName === null) {
        // add an empty string if the sub-account field is null
        t = { ...approval, approved, outstandingApproval };
        t.subAccountName = '';
        return t;
      } else return { ...approval, approved, outstandingApproval };
    });
    setApprovals(temp);
    setLoading(false)
  }, [requestsList]);

  const renderApprovals = async () => {
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      console.log(accessToken);
    }
    const { success, data } = await GetPendingRequests(accessToken, loggedInUser.id, true);
    if (success && data?.length > 0) {
      handlePendingRequestsUpdate(data);
    } else {
      enqueueSnackbar(data, {
        variant: 'error'
      });
    }
  }

  const handleColumnSort = tableColumn => {
    const isAsc = orderBy === tableColumn && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(tableColumn);
  };

  const handleOpenRequest = async () => {
    setEditApproverThreshold(true);
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      return;
    }
    const { success, data } = await GetAllUsersAssociatedWithAGroup(
      accessToken,
      false
    );
    if (success) {
      let approversByGroup = {};
      data.forEach(group => {
        let ownerCount = group.users.filter(user => user.roleName === 'owner');
        approversByGroup[group.name] = ownerCount.length;
      });
      setGroupOwnersCount(approversByGroup[groupName]);
    } else {
      enqueueSnackbar(data, {
        variant: 'error'
      });
    }
  };

  const handleEditApprovalThreshold = async groupName => {
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      console.log(accessToken);
    }
    const { success, data } = await UpdateApprovalThreshold(
      accessToken,
      groupId,
      approverThreshold
    );
    const isApprovalActionRequired = data.isApprovalActionRequired;
    if (success) {
      // an approval threshold of 1 allows the threshold to update without additional approval
      if (!isApprovalActionRequired)
        enqueueSnackbar(
          'The number of approvals from account owners needed has been updated successfully.',
          {
            variant: 'success'
          }
        );
      const { success, data } = await GetPendingRequests(accessToken, loggedInUser.id);
      if (success && data?.length > 0) {
        handlePendingRequestsUpdate(data);
      } else {
        enqueueSnackbar(data, {
          variant: 'error'
        });
      }
    } else {
      enqueueSnackbar(data, {
        variant: 'error'
      });
    }
    setEditApproverThreshold(false);
  };

  const handleApprovalRequest = async (approvalRequestId, action) => {
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      return;
    }
    const { success, data } = await ApprovalRequest(
      approvalRequestId, 
      accessToken, 
      action
    );

    if (success) {
      renderApprovals()
      setPendingApprovalThresholdUpdate(null)
      enqueueSnackbar(
        `You have successfully ${action === "approve" ? 'approved' : 'rejected'} the change request.`,
        {
          variant: 'success'
        }
      )
    } else {
      enqueueSnackbar(data, {
        variant: 'error'
      });
    }
  };

  const renderExpirationStyle = (expiresAt) => {
    switch (expiresAt) {
      case "in 3 days":
        return "m-text is-accent"
      case "in 2 days":
        return "m-text is-warning"
      default:
        return "m-text is-danger"
    }
  }

  const handleCopyWalletAddress = (address) => {
    navigator.clipboard.writeText(address);
    setShowAddressCopiedIcon(true)
    setTimeout(() => setShowAddressCopiedIcon(false), 3000);
  }

  const renderPayoutPercentageUpdateTable = (walletChanges, coin) => {
    return Object.keys(walletChanges).map((key) =>
      <>
        <Typography variant='h6' style={{ padding: '10px 0'}}>{t(`${key}`)}</Typography>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow style={{ backgroundColor: 'var(--color-background)'}}>
                <TableCell align="left">{t('label')}</TableCell>
                <TableCell align="left">{t('address')}</TableCell>
                <TableCell align="left" style={{ whiteSpace: 'nowrap', paddingRight: 0}}>{t('dateAdded')}</TableCell>
                <TableCell align="left">{t('status')}</TableCell>
                <TableCell align="center"  style={{ paddingRight: 42, whiteSpace: 'nowrap'}}>{key === 'beforeWalletChanges' ? t('allocationPercent') : t('newAllocationPercent')}</TableCell>
                <TableCell align="right" style={{ textAlign: 'center', whiteSpace: 'nowrap'}}>{t('autoWithdraw')}</TableCell>
              </TableRow>
            </TableHead>
          {walletChanges[key]?.length &&
            walletChanges[key].map((wallet, index) => {                                
              return <TableRow style={{ backgroundColor: 'var(--color-background)'}} key={wallet.walletLabel}>
                <TableCell align="left">{ wallet.walletLabel }</TableCell>
                <TableCell align="left">
                  <Tooltip
                    TransitionComponent={Zoom} 
                    arrow 
                    interactive 
                    classes={{ tooltip: classes.noMaxWidth }} 
                    placement="top" 
                    enterTouchDelay={0} 
                    enterDelay={0} 
                    maxWidth={'none'} 
                    onClose={() => setTimeout(() => setShowAddressCopiedIcon(false), 250)}
                    title={
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Typography variant="subtitle1" style={{ paddingLeft:"8px"}}>{wallet.walletAddress}</Typography>
                        <div style={{ position: 'relative'}}>
                          <Typography 
                            variant='subtitle1' 
                            style={{ position: 'absolute', top: -2, left: showAddressCopiedIcon ? 2 : 7}}
                          >
                            { showAddressCopiedIcon ? 'Copied' : 'Copy'}
                          </Typography>
                          <IconButton 
                            onClick={() => handleCopyWalletAddress(wallet.walletAddress)}
                            disabled={showAddressCopiedIcon}
                            style={{marginTop: 7}}
                            arrow
                            size="large"
                          >
                            <SvgIcon fontSize="small">
                              {!showAddressCopiedIcon ?  <Copy />  :<CheckCircle2 /> }
                            </SvgIcon>
                          </IconButton>
                        </div>    
                      </Box>}>
                    <a style={{color:"inherit", variant:"h4"}} 
                      href={`${btcInfoWebsite}/address/${wallet.walletAddress}`}
                      target="_blank" rel="noopener noreferrer">{wallet.walletAddress ? wallet.walletAddress.slice(0,4)+"..."+wallet.walletAddress.slice(-4):"-"}
                    </a>
                  </Tooltip>
                </TableCell>
                <TableCell align="left" style={{ whiteSpace: 'nowrap', paddingRight: 0}}>{ wallet.dateAdded }</TableCell>
                <TableCell align="left">{ wallet.status }</TableCell>
                <TableCell align="center"  style={{ paddingRight: 42, whiteSpace: 'nowrap'}}>
                  <TextField 
                    variant="outlined"
                    size="small"
                    style={{ width: 130 }} 
                    sx={{ 
                      '& .MuiInputBase-input': {
                      color: 'white !important',
                        '-webkit-text-fill-color': 'inherit'
                      },
                      '& .MuiTypography-root':{
                        color: 'white !important'
                      },
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    value={wallet.allocationPercent}
                    disabled
                  >
                    </TextField>
                </TableCell>
                <TableCell align="right" style={{ textAlign: 'center', whiteSpace: 'nowrap'}}><Checkbox checked={wallet.allocationPercent > 0 ? true : false} disabled /></TableCell>
              </TableRow>
            })
          }
          </Table>
        </TableContainer>
      </>
       
    )
  }

  const renderPayoutPercentageUpdateCard = (walletChanges, coin) => {
    return Object.keys(walletChanges).map((key) =>
      <>
        <Typography variant='h5' style={{ paddingTop: key === 'beforeWalletChanges' ?  20 : 10, paddingBottom: 10 }}>{t(`${key}`)}</Typography>
        <Table>
            {
              walletChanges[key]?.length && walletChanges[key].map((wallet) => {
                return (<TableBody>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{t('label')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>{wallet.walletLabel}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{t('address')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>{ <a style={{color:"inherit", variant:"h4"}} 
                        href={`${btcInfoWebsite}/address/${wallet.walletAddress}`}
                        target="_blank" rel="noopener noreferrer">{wallet.walletAddress ? wallet.walletAddress.slice(0,4)+"..."+wallet.walletAddress.slice(-4):"-"}
                      </a>}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{t('dateAdded')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>{wallet.dateAdded}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{t('status')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>{wallet.status}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{key === 'beforeWalletChanges' ? t('allocationPercent') : t('newAllocationPercent')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>
                        <TextField 
                          variant="outlined"
                          size="small"
                          style={{ width: 110 }} 
                          sx={{
                              color: 'white !important',
                            '& .MuiInputBase-input': {
                            color: 'white !important',
                              '-webkit-text-fill-color': 'inherit'
                            },
                            '& .MuiTypography-root':{
                              color: 'white !important'
                            },
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          value={wallet.allocationPercent}
                          disabled
                        >
                        </TextField>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" className={classes.mobileLabel}>{t('autoWithdraw')}</TableCell>
                      <TableCell align="right" className={classes.mobileValue}>
                        <Checkbox checked={wallet.allocationPercent > 0 ? true : false} style={{ padding: '5px 0' }}disabled />
                      </TableCell>
                    </TableRow>
                    <TableRow><div style={{padding: 10}}></div></TableRow>
                  </TableBody>)
              })
            }
        </Table>
      </>)
  }

  const renderApprovalsTable = () => {
    function Row({ row } = props) {
      return (
        <React.Fragment>
          <TableRow>
            <TableCell align="left">
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() =>
                  setOpenRows(prevArray =>
                    prevArray.includes(row.approvalRequestId)
                      ? prevArray.filter(i => row.approvalRequestId !== i)
                      : [...prevArray, row.approvalRequestId]
                  )
                }
              >
                <KeyboardArrowUpIcon
                  style={{
                    transform: openRows.includes(row.approvalRequestId)
                      ? 'rotate(0deg)'
                      : 'rotate(180deg)'
                  }}
                />
              </IconButton>
            </TableCell>
            <TableCell align="left" className={renderExpirationStyle(dayjs().to(dayjs(row.expiresAt)))} style={{textTransform: "capitalize"}}>
              {dayjs().to(dayjs(row.expiresAt))}
            </TableCell>
            <TableCell align="left" style={{textTransform: "capitalize"}}>{row.requestType}</TableCell>
            <TableCell align="left">{row.subAccountName}</TableCell>
            <TableCell align="left">
              <ClickAwayListener
                onClickAway={() => setDisplayApproverList(null)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-m)',
                  }}
                >
                    <p
                      className="m-text"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setDisplayApproverList(row.approvalRequestId)
                      }
                    >
                      {row.status}
                    </p>
                    <Tooltip
                      open={displayApproverList === row.approvalRequestId}
                      arrow
                      placement={ mediumDevice ? 'bottom' : 'right'}
                      title={
                      <div>
                        <p style={{ padding: '1rem' }} className="m-text">
                          Requires approval from {row.outstandingApproval} of the
                          following:
                        </p>
                        <div
                          style={{
                            borderTop: '1px solid rgba(81, 81, 81, 1)',
                            padding: '0 1rem 1rem 1rem'
                          }}
                        >
                          {JSON.parse(row.approvers).map((approver, index) => {
                            return (
                              <div
                                key={approver.approvalRequestId}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '.8rem 1fr',
                                  gridTemplateRows: '1rem',
                                  padding: 'var(--space-s) 0 0 var(--space-s)'
                                }}
                              >
                                {approver.action === 'APPROVED' ? (
                                  <div
                                    style={{
                                      width: '.8rem',
                                      height: '1rem',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <CheckCircle
                                      style={{
                                        width: '.8rem',
                                        height: '.8rem',
                                        transform: 'translate(0, 2px)',
                                        color: 'var(--color-menu-selected)'
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div />
                                )}
                                <p
                                  className="m-text"
                                  style={{ paddingLeft: 'var(--space-s)' }}
                                >
                                  {approver.fullName}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    }
                  >
                    <Info
                      style={{ width: '1.2rem', cursor: 'pointer' }}
                      onClick={() =>
                        setDisplayApproverList(row.approvalRequestId)
                      }
                    />
                </Tooltip>
                </div>
              </ClickAwayListener>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    }

    return (
      <TableBody>
        {approvals &&
          applySorting(approvals, getComparator(order, orderBy)).map(
            (approval) => {
              return (
                <React.Fragment key={approval.approvalRequestId}>
                  <Row row={approval} />
                  <TableCell
                    colSpan={6}
                    style={{
                      padding: '0'
                    }}
                  >
                    <Collapse
                      in={openRows.includes(approval.approvalRequestId)}
                      collapsedSize={'0px'}
                      style={{
                        cursor: openRows.includes(approval.approvalRequestId)
                          ? ''
                          : 'pointer',
                        borderBottom: openRows.includes(
                          approval.approvalRequestId
                        )
                          ? '1px solid rgba(81, 81, 81, 1)'
                          : 'none',
                        backgroundColor: 'var(--neutral-3)'
                      }}
                    >
                      <div>
                        {(!approval.requestType.match('Update Payout Settings')) ? (
                          <div>
                            <TableContainer component={Paper}>
                              <Table
                                className={ classes.metalTable }
                                style={{
                                  backgroundColor: 'var(--neutral-1)'
                                }}
                                size="small"
                              >
                                <TableHead>
                                  {approval.requestType.match('Update User\'s Default Role') ? 
                                  <>
                                    <TableCell style={{ width:400}}>{t('fullName')}</TableCell>
                                    <TableCell style={{ width:400}}>{t('emailAddress')}</TableCell>
                                    <TableCell style={{ width:200}}>{t('currentDefaultRole')}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>{t('newDefaultRole')}</TableCell>
                                  </>
                                  : approval.requestType.match('Update Approval Threshold') ? 
                                  <>
                                    <TableCell style={{ width:400}}>{t('groupName')}</TableCell>
                                    <TableCell style={{ width:200}}>{t('currentThreshold')}</TableCell>
                                    <TableCell style={{ width:150}}></TableCell>
                                    <TableCell style={{ width:200}}>{t('newThreshold')}</TableCell>
                                    <TableCell></TableCell> 
                                  </> 
                                  : approval.requestType.match('Create Sub-account') ? 
                                  <>
                                    <TableCell style={{ width:400}}>{t('addressLabel')}</TableCell>
                                    <TableCell style={{ width:350}}>{t('subAccount')}</TableCell>
                                    <TableCell style={{ width:400}}>{t('payoutAddress')}</TableCell>
                                    <TableCell>{t('users')}</TableCell>
                                  </> 
                                  : approval.requestType.match('Add User') ? 
                                  <>
                                    <TableCell style={{ width:375}}>{t('fullName')}</TableCell>
                                    <TableCell style={{ width:400}}>{t('emailAddress')}</TableCell>
                                    <TableCell style={{ width:350}}>{t('defaultRole')}</TableCell>
                                    <TableCell>{t('subAccounts')}</TableCell>
                                  </> 
                                  : Object.keys(
                                    JSON.parse(approval.details)
                                  ).map((headerTitle, index) => {
                                    return (
                                      <TableCell key={index}>
                                        <p>{ t(`${headerTitle}`)} </p>
                                      </TableCell>
                                    );
                                  })}
                                </TableHead>
                                { approval.requestType.match('Update User\'s Default Role') ?
                                <TableRow>
                                  <TableCell>{ JSON.parse(approval.details)['fullName'] }</TableCell>
                                  <TableCell>{ JSON.parse(approval.details)['emailAddress'] }</TableCell>
                                  <TableCell>{ JSON.parse(approval.details)['currentDefaultRole'] }</TableCell>
                                  <TableCell><ArrowRight style={{  color: 'var(--color-status-warning)', paddingTop: 5}} /></TableCell>
                                  <TableCell  style={{  color: 'var(--color-status-warning)'}}>{ JSON.parse(approval.details)['newDefaultRole'] }</TableCell>
                                </TableRow>
                                : approval.requestType.match('Update Approval Threshold') ?
                                <TableRow>
                                  <TableCell>{ JSON.parse(approval.details)['groupName'] }</TableCell>
                                  <TableCell>{ JSON.parse(approval.details)['currentApprovalThreshold'] }</TableCell>
                                  <TableCell><ArrowRight style={{  color: 'var(--color-status-warning)', paddingTop: 5}} /></TableCell>
                                  <TableCell style={{  color: 'var(--color-status-warning)'}}>{ JSON.parse(approval.details)['newApprovalThreshold'] }</TableCell>
                                  <TableCell></TableCell>
                                </TableRow> 
                                : approval.requestType.match('Create Sub-account') ?
                                <TableRow>
                                  <TableCell>{ JSON.parse(approval.details)['label'] }</TableCell>
                                  <TableCell>{ JSON.parse(approval.details)['subAccountName'] }</TableCell>
                                  <TableCell>{ JSON.parse(approval.details)['address'] }</TableCell>
                                  <TableCell>
                                    <p
                                      aria-describedby={approval.approvalRequestId} 
                                      className={`${classes.chip} m-text m-chip m-info`}
                                      style={{borderRadius: "10px"}}
                                      data-color-status="info"
                                      onClick={(e)=>handleClick(e, approval.approvalRequestId)}
                                    >
                                      {`${JSON.parse(JSON.parse(approval.details)['accessRequest']).length} users`}
                                    </p>
                                      <Popover
                                        open={openedPopoverId === approval.approvalRequestId}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'right',
                                        }}    
                                        sx={{
                                          '& .MuiPopover-paper': {
                                            padding: 0
                                          }
                                        }}                                    
                                      >
                                        <div 
                                          className='m-text'
                                          style={{
                                            borderRadius: "4px",
                                            backgroundColor: "#161B26",
                                            borderColor: "var(--color-border-info)",
                                        }}>
                                          <p 
                                            style={{ padding: '1rem' }}>
                                            Users on this Sub-Account:
                                          </p>
                                          <div
                                            style={{
                                              padding: '0 1rem 1rem 1rem'
                                            }}
                                          >
                                            <TableContainer>
                                              <Table
                                                sx={{
                                                  '& td:first-of-type': {
                                                    borderBottom: "none",
                                                    paddingLeft: 0
                                                  },
                                                  '& td:last-of-type': {
                                                    borderBottom: "none",
                                                    paddingRight: 0,
                                                    textAlign: "end"
                                                  },
                                                  backgroundColor: '#161B26'
                                                }}
                                                size="small"
                                              >
                                                <TableBody>
                                                  {
                                                    applySorting(JSON.parse(JSON.parse(approval.details)['accessRequest']), getComparator("asc", "fullName")).map(user => {
                                                      return (
                                                        <TableRow className='m-text' key={user.userId} >
                                                          <TableCell className='m-text'>{user.fullName}</TableCell>
                                                          <TableCell className='m-text' style={{textTransform: "capitalize"}}>{user.roleName}</TableCell>
                                                        </TableRow>
                                                      )
                                                    })
                                                  }
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </div>
                                        </div>
                                      </Popover>
                                  </TableCell>
                                </TableRow> 
                                : approval.requestType.match('Add User') ?
                                <TableRow>
                                <TableCell>{ JSON.parse(approval.details)['fullName'] }</TableCell>
                                <TableCell>{ JSON.parse(approval.details)['emailAddress'] }</TableCell>
                                <TableCell>{ JSON.parse(approval.details)['defaultRole'] }</TableCell>
                                <TableCell>
                                  <> {
                                      JSON.parse(approval.details)['subAccounts']?.length >= 1 ?
                                      <p
                                        aria-describedby={approval.approvalRequestId} 
                                        className={`${classes.chip} m-text m-chip m-info`}
                                        style={{borderRadius: "10px"}}
                                        data-color-status="info"
                                        onClick={(e)=>handleClick(e, approval.approvalRequestId)}
                                      >
                                        {`${JSON.parse(approval.details)['subAccounts']?.length} ${JSON.parse(approval.details)['subAccounts']?.length === 1 ? 'sub-account' : 'sub-accounts'}`}
                                      </p> : <p
                                        aria-describedby={approval.approvalRequestId} 
                                        className='m-text'
                                      >
                                        All Sub-Accounts
                                      </p>
                                    }                                  
                                  </>
                                    <Popover
                                      open={openedPopoverId === approval.approvalRequestId}
                                      anchorEl={anchorEl}
                                      onClose={handleClose}
                                      anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                      }}
                                      transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                      }}    
                                      sx={{
                                        '& .MuiPopover-paper': {
                                          padding: 0
                                        }
                                      }}                                    
                                    >
                                      <div 
                                        className='m-text'
                                        style={{
                                          borderRadius: "4px",
                                          backgroundColor: "#161B26",
                                          borderColor: "var(--color-border-info)",
                                      }}>
                                        <p 
                                          style={{ padding: '1rem' }}>
                                          Add user to the following sub-account(s):
                                        </p>
                                        <div
                                          style={{
                                            padding: '0 1rem 1rem 1rem'
                                          }}
                                        >
                                          <TableContainer>
                                            <Table
                                              sx={{
                                                '& td:first-of-type': {
                                                  borderBottom: "none",
                                                  paddingLeft: 0
                                                },
                                                '& td:last-of-type': {
                                                  borderBottom: "none",
                                                  paddingRight: 0,
                                                  textAlign: "end"
                                                },
                                                backgroundColor: '#161B26'
                                              }}
                                              size="small"
                                            >
                                              <TableBody>
                                                {
                                                 JSON.parse(approval.details)['subAccounts']?.map((account) => {
                                                    return (
                                                      <TableRow key={account + approval.approvalRequestId} >
                                                         <TableCell className='m-text' style={{ textAlign: 'left'}}><p>{account}</p></TableCell>
                                                      </TableRow>
                                                    )
                                                  })
                                                }
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </div>
                                      </div>
                                    </Popover>
                                </TableCell>
                              </TableRow> 
                                : <TableRow>
                                 { Object.keys(JSON.parse(approval.details)).map(
                                  (tableData, index) => {
                                    return (
                                      <TableCell key={index} style={{ width: index === 0 ? 400 : 'auto' }}>
                                          <p>
                                            {
                                              JSON.parse(approval.details)[
                                                tableData
                                              ]
                                            }
                                          </p>
                                      </TableCell>
                                    );
                                  }
                                )
                                }
                                </TableRow>
                                }
                              </Table>
                            </TableContainer>
                          </div>
                        ) : (
                          <>
                            {renderPayoutPercentageUpdateTable(JSON.parse(approval.details), approval.coin)}
                          </>
                        )}
                        
                      </div>
                      { (!JSON.parse(approval.approvers).filter(i => i.action === "APPROVED").filter(j => j.fullName === loggedInUser.fullName).length) &&
                        (JSON.parse(approval.details)?.fullName !== loggedInUser.fullName) && approval.isApprovalActionRequired &&
                        <div className="m-stack is-horizontal" style={{justifyContent: "flex-end"}}>
                          <button 
                            onClick={()=>{handleApprovalRequest(approval.approvalRequestId, "reject")}}
                            className='m-button is-destructive' 
                            style={{minWidth: "75px", margin: "16px 8px", justifyContent: "center"}}>Reject</button>
                          <button
                            onClick={()=>{handleApprovalRequest(approval.approvalRequestId, "approve")}} 
                            className='m-button' 
                            style={{minWidth: "75px", margin: "16px 8px", justifyContent: "center"}}>Approve</button>
                        </div>       
                      }
                    </Collapse>
                  </TableCell>
                </React.Fragment>
              );
            }
          )}
      </TableBody>
    );
  };
  
  const renderApprovalsCards = () => {      
      return (
        <>
          {approvals &&
            applySorting(approvals, getComparator(usersOrderMobile, usersOrderByMobile)).map(
              (approval, index) => {
                return (
                  <Card key={approval.approvalRequestId} variant='outlined' sx={{ marginTop: "10px", backgroundColor: "var(--neutral-2)", borderColor: "var(--color-border)"}}>
                    <CardHeader
                      sx={{paddingBottom: "0", textTransform: "capitalize"}}
                      title={approval.requestType}
                      subheader={
                        <Typography className={renderExpirationStyle(dayjs().to(dayjs(approval.expiresAt)))} style={{textTransform: "capitalize", marginTop: "4px"}}>
                          {t('expiration')} {dayjs().to(dayjs(approval.expiresAt))}
                        </Typography>}
                    />
                      <CardContent>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-s)',
                                  }}
                                >
                                    <p
                                      className="m-text"
                                      style={{ cursor: 'pointer' }}
                                      onClick={(e) =>
                                        handleCardClick(e, approval.approvalRequestId)
                                      }
                                    >
                                      {approval.status}
                                    </p>
                                    <Info
                                      style={{ width: '1.2rem', cursor: 'pointer' }}
                                      onClick={(e) =>
                                        handleCardClick(e, approval.approvalRequestId)
                                      }
                                    />
                                    <Popover
                                        open={displayApproverList === approval.approvalRequestId}
                                        anchorEl={anchorElCard}
                                        onClose={handleCardClose}
                                        anchorOrigin={{
                                          vertical: 'center',
                                          horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                          vertical: 'center',
                                          horizontal: 'left',
                                        }} 
                                        sx={{
                                          '& .MuiPopover-paper': {
                                            padding: 0
                                          }
                                        }}                                    
                                      >
                                        <div 
                                          className='m-text'
                                          style={{
                                            backgroundColor: "var(--neutral-2)",
                                        }}>
                                            <p style={{ padding: '1rem' }} className="m-text">
                                              Requires approval from {approval.outstandingApproval} of the
                                              following:
                                            </p>
                                            <div
                                              style={{
                                                borderTop: '1px solid rgba(81, 81, 81, 1)',
                                                padding: '0 1rem 1rem 1rem'
                                              }}
                                            >
                                            <TableContainer  sx={{overflowX: "unset"}}>
                                              <Table
                                                sx={{
                                                  '& td:first-of-type': {
                                                    borderBottom: "none",
                                                    paddingLeft: 0
                                                  },
                                                  '& td:last-of-type': {
                                                    borderBottom: "none",
                                                    paddingRight: 0,
                                                    textAlign: "end"
                                                  },
                                                  backgroundColor: "var(--neutral-2)"
                                                }}
                                                size="small"
                                              >
                                                <TableBody>
                                                {JSON.parse(approval.approvers).map((approver, index) => {
                                                return (
                                                  <div
                                                    key={approver.approvalRequestId}
                                                    style={{
                                                      display: 'grid',
                                                      gridTemplateColumns: '.8rem 1fr',
                                                      gridTemplateRows: '1rem',
                                                      padding: 'var(--space-s) 0 0 var(--space-s)'
                                                    }}
                                                  >
                                                    {approver.action === 'APPROVED' ? (
                                                      <div
                                                        style={{
                                                          width: '.8rem',
                                                          height: '1rem',
                                                          display: 'flex',
                                                          alignItems: 'center'
                                                        }}
                                                      >
                                                        <CheckCircle
                                                          style={{
                                                            width: '.8rem',
                                                            height: '.8rem',
                                                            transform: 'translate(0, 2px)',
                                                            color: 'var(--color-menu-selected)'
                                                          }}
                                                        />
                                                      </div>
                                                    ) : (
                                                      <div />
                                                    )}
                                                    <p
                                                      className="m-text"
                                                      style={{ paddingLeft: 'var(--space-s)' }}
                                                    >
                                                      {approver.fullName}
                                                    </p>
                                                  </div>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </div>
                                    </div>
                                  </Popover>
                                </div>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        {!approval.requestType.match('Update Payout Settings') ? <Table>
                          <TableBody>
                            {
                              approval.requestType.match('Update User\'s Default Role') ? 
                                <>
                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('fullName')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['fullName']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('emailAddress')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-all"}} variant='h6'>{JSON.parse(approval.details)['emailAddress']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('currentDefaultRole')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{JSON.parse(approval.details)['currentDefaultRole']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('newDefaultRole')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{JSON.parse(approval.details)['newDefaultRole']}</Typography>
                                    </TableCell>
                                  </TableRow>
                                </>
                                : approval.requestType.match('Update Approval Threshold') ? 
                                <>
                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('groupName')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['groupName']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('currentThreshold')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{JSON.parse(approval.details)['currentApprovalThreshold']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('newThreshold')}</Typography>
                                    </TableCell>
                                    <TableCell style={{  color: 'var(--color-status-warning)'}} align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{JSON.parse(approval.details)['newApprovalThreshold']}</Typography>
                                    </TableCell>
                                  </TableRow>
                                </> 
                                : approval.requestType.match('Create Sub-account') ?
                                <>
                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('label')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['label']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('subAccount')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['subAccountName']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('address')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['address']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('users')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <p
                                        aria-describedby={approval.approvalRequestId} 
                                        className={`${classes.chip} m-text m-chip m-info`}
                                        style={{borderRadius: "10px"}}
                                        data-color-status="info"
                                        onClick={(e)=>handleClick(e, approval.approvalRequestId)}
                                      >
                                        {`${JSON.parse(JSON.parse(approval.details)['accessRequest']).length} users`}
                                      </p>
                                      <Popover
                                        open={openedPopoverId === approval.approvalRequestId}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                          vertical: 'center',
                                          horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                          vertical: 'center',
                                          horizontal: 'right',
                                        }} 
                                        sx={{
                                          '& .MuiPopover-paper': {
                                            padding: 0
                                          }
                                        }}                                    
                                      >
                                        <div 
                                          className='m-text'
                                          style={{
                                            borderRadius: "4px",
                                            backgroundColor: "#161B26",
                                            borderColor: "var(--color-border-info)",
                                        }}>
                                          <p 
                                            style={{ padding: '1rem' }}>
                                            Users on this Sub-Account:
                                          </p>
                                          <div
                                            style={{
                                              padding: '0 1rem 1rem 1rem'
                                            }}
                                          >
                                            <TableContainer>
                                              <Table
                                                sx={{
                                                  '& td:first-of-type': {
                                                    borderBottom: "none",
                                                    paddingLeft: 0
                                                  },
                                                  '& td:last-of-type': {
                                                    borderBottom: "none",
                                                    paddingRight: 0,
                                                    textAlign: "end"
                                                  },
                                                  backgroundColor: '#161B26'
                                                }}
                                                size="small"
                                              >
                                                <TableBody>
                                                  {
                                                    applySorting(JSON.parse(JSON.parse(approval.details)['accessRequest']), getComparator("asc", "fullName")).map(user => {
                                                      return (
                                                        <TableRow className='m-text' key={user.userId} >
                                                          <TableCell className='m-text' >{user.fullName}</TableCell>
                                                          <TableCell className='m-text' style={{textTransform: "capitalize"}}>{user.roleName}</TableCell>
                                                        </TableRow>
                                                      )
                                                    })
                                                  }
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </div>
                                        </div>
                                      </Popover>
                                    </TableCell>
                                  </TableRow>
                                </>
                                : approval.requestType.match('Add User') ? 
                                <>
                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('fullName')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['fullName']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('emailAddress')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['emailAddress']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('defaultRole')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography style={{wordBreak: "break-word"}} variant='h6'>{JSON.parse(approval.details)['defaultRole']}</Typography>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <Typography variant='h6'>{t('subAccounts')}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                     <>{ JSON.parse(approval.details)['subAccounts']?.length >= 1 ?
                                      <p
                                        aria-describedby={approval.approvalRequestId} 
                                        className={`${classes.chip} m-text m-chip m-info`}
                                        style={{borderRadius: "10px"}}
                                        data-color-status="info"
                                        onClick={(e)=>handleClick(e, approval.approvalRequestId)}
                                      >
                                        {`${JSON.parse(approval.details)['subAccounts']?.length} ${JSON.parse(approval.details)['subAccounts']?.length === 1 ? 'sub-account' : 'sub-accounts'}`}
                                      </p> : <p
                                        aria-describedby={approval.approvalRequestId} 
                                        className='m-text'
                                      >
                                        All Sub-Accounts
                                      </p>
                                     }                          
                                     </> 
                                     <Popover
                                      open={openedPopoverId === approval.approvalRequestId}
                                      anchorEl={anchorEl}
                                      onClose={handleClose}
                                      anchorOrigin={{
                                        vertical: 'center',
                                        horizontal: 'left',
                                      }}
                                      transformOrigin={{
                                        vertical: 'center',
                                        horizontal: 'right',
                                      }}    
                                      sx={{
                                        '& .MuiPopover-paper': {
                                          padding: 0
                                        }
                                      }}                                    
                                    >
                                      <div 
                                        className='m-text'
                                        style={{
                                          borderRadius: "4px",
                                          backgroundColor: "#161B26",
                                          borderColor: "var(--color-border-info)",
                                      }}>
                                        <p 
                                          style={{ padding: '1rem' }}>
                                          Add user to the following sub-account(s):
                                        </p>
                                        <div
                                          style={{
                                            padding: '0 1rem 1rem 1rem'
                                          }}
                                        >
                                          <TableContainer>
                                            <Table
                                              sx={{
                                                '& td:first-of-type': {
                                                  borderBottom: "none",
                                                  paddingLeft: 0
                                                },
                                                '& td:last-of-type': {
                                                  borderBottom: "none",
                                                  paddingRight: 0,
                                                  textAlign: "end"
                                                },
                                                backgroundColor: '#161B26'
                                              }}
                                              size="small"
                                            >
                                              <TableBody>
                                                {
                                                 JSON.parse(approval.details)['subAccounts']?.map((account) => {
                                                    return (
                                                      <TableRow key={account + approval.approvalRequestId} >
                                                         <TableCell className='m-text' style={{ textAlign: 'left'}}><p>{account}</p></TableCell>
                                                      </TableRow>
                                                    )
                                                  })
                                                }
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </div>
                                      </div>
                                    </Popover>
                                    </TableCell>
                                  </TableRow>
                                </> 
                                : <>
                                  {
                                    approval.subAccountName &&
                                      <TableRow>
                                        <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                          <Typography variant='h6'>{t('subAccount')}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                          <Typography style={{wordBreak: "break-all"}} variant='h6'>{approval.subAccountName}</Typography>
                                        </TableCell>
                                      </TableRow>
                                  }
                                  {
                                    Object.keys(
                                      JSON.parse(approval.details)
                                    ).map((headerTitle, index) => {
                                      return (
                                        <TableRow key={index}>
                                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                                            <Typography variant='h6'>{t(headerTitle)}</Typography>
                                          </TableCell>
                                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                            <Typography style={{wordBreak: "break-all"}} variant='h6'>{
                                                JSON.parse(approval.details)[
                                                  headerTitle
                                                ]
                                              }</Typography>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  }
                                </> 
                            }
                            </TableBody>
                          </Table>
                          :
                          <>
                            {renderPayoutPercentageUpdateCard(JSON.parse(approval.details), approval.coin)}
                          </>
                          }
                            { (!JSON.parse(approval.approvers).filter(i => i.action === "APPROVED").filter(j => j.fullName === loggedInUser.fullName).length) &&
                              (JSON.parse(approval.details)?.fullName !== loggedInUser.fullName) && approval.isApprovalActionRequired && 
                              <Table>
                                <TableBody>
                                  <TableRow>
                                    <TableCell align="center" sx={{paddingLeft: 0, paddingRight: 0}}>
                                      <div className="m-stack is-horizontal" style={{justifyContent: "space-evenly"}}>
                                        <button 
                                          onClick={()=>{handleApprovalRequest(approval.approvalRequestId, "reject")}}
                                          className='m-button is-destructive' 
                                          style={{minWidth: "110px", margin: "16px 8px", justifyContent: "center"}}>Reject</button>
                                        <button
                                          onClick={()=>{handleApprovalRequest(approval.approvalRequestId, "approve")}} 
                                          className='m-button' 
                                          style={{minWidth: "110px", margin: "16px 8px", justifyContent: "center"}}>Approve</button>
                                      </div>  
                                    </TableCell>
                                    
                                  </TableRow>     
                                </TableBody>
                              </Table>
                            }  
                      </CardContent>
                  </Card>
                );
              }
            )}
        </>
      );
  }

  return (
    <Card
      style={{
        backgroundColor: 'var(--neutral-3)',
        padding: '0 10px var(--space-m)'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: mediumDevice ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: mediumDevice ? 'flex-start' : 'center',
          minHeight: 'var(--space-xxl)',
          maxHeight: 'auto'
        }}
      >
        {!loading && groupId && (
          <>
            {pendingApprovalThresholdUpdate !== null ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  flexDirection: 'row',
                  width: mediumDevice ? "100%" : "inherit",
                  justifyContent: mediumDevice ? "flex-end" : 'space-between',
                  alignItems: 'center',
                  gap: '10px',
                  padding: 'var(--space-m) 0'
                }}
              >
                <AlertTriangle
                  style={{
                    color: 'var(--color-text-warning)',
                    minWidth: 'var(--space-m)',
                  }}
                  size={16}
                />
                <p
                  className="m-text"
                  style={{ color: 'var(--color-text-warning)', maxWidth: "90%" }}
                >
                  {`Approver threshold will change from ${
                    JSON.parse(pendingApprovalThresholdUpdate?.details)[
                      'currentApprovalThreshold'
                    ]
                  } to ${
                    JSON.parse(pendingApprovalThresholdUpdate?.details)[
                      'newApprovalThreshold'
                    ]
                  } when ${
                    pendingApprovalThresholdUpdate?.status?.match(
                      /\/(\d+)\)/
                    )[1]
                  } owners approve
                  the change`}
                </p>
              </div>
            ) : !loading && !editApproverThreshold ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: mediumDevice ? "flex-end" : "inherit",
                  width: mediumDevice ? "100%" : "inherit",
                  alignItems: 'center',
                  gap: 'var(--space-m)',
                  padding: mediumDevice ? 'var(--space-m) 0' : ''
                }}
              >
                <p className="m-text">
                  {groupApprovalThreshold} account owner
                  {groupApprovalThreshold > 1 ? 's' : ''}{' '}
                  {groupApprovalThreshold > 1 ? 'are' : 'is'} required to
                  approve all changes
                </p>
                <IconButton
                  aria-label="learn more about approval threshold"
                  size="small"
                  style={{ cursor: 'pointer' }}
                >
                  <RouterLink
                    style={{ display: 'flex' }}
                    target="_blank"
                    rel="noopener noreferrer"
                    to={
                      'https://pool-faq.foundrydigital.com/what-is-the-request-approve-feature'
                    }
                  >
                    <CircleHelp
                      className="m-text"
                      size={0.8}
                      style={{ minWidth: 'var(--space-m)' }}
                    />
                  </RouterLink>
                </IconButton>
                {loggedInUser.groups.filter(
                  // only show threshld edit btn if user is owner
                  g => g.group.name === groupName
                )[0]?.userRole?.roleName === 'mining_account_owner' && (
                  <IconButton
                    aria-label="edit group approver threshold"
                    size="small"
                    onClick={handleOpenRequest}
                    style={{ cursor: 'pointer' }}
                  >
                    <Pen className="m-text" size={17} />
                  </IconButton>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  width: mediumDevice ? "100%" : "inherit",
                  flexDirection: mediumDevice ? 'column' : 'row',
                  alignItems:
                    mediumDevice ? 'flex-start' : 'center',
                  gap: 'var(--space-s)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 'var(--space-s)',
                    padding:
                      mediumDevice ? 'var(--space-m) 0 0 0' : ''
                  }}
                >
                  <TextField
                    className={
                      approverThreshold > groupOwnersCount ? 'errorAnime' : ''
                    }
                    value={approverThreshold}
                    onChange={e => setApproverThreshold(e.target.value)}
                    inputProps={{
                      style: {
                        width: '3ch',
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bolder',
                        padding: '0',
                        position: 'relative'
                      }
                    }}
                    helperText={
                      <p
                        style={{
                          color: '#f44336',
                          transform: 'translate(-40%,-5px)',
                          whiteSpace: 'nowrap',
                          position: 'absolute',
                          transition:
                            'max-height .3s ease-in-out, opacity .3s ease-in-out',
                          maxHeight:
                            approverThreshold > groupOwnersCount
                              ? '2ch'
                              : '0',
                          opacity:
                            approverThreshold > groupOwnersCount ? 1 : 0
                        }}
                      >
                        Threshold exceeds number of owners.
                      </p>
                    }
                  />

                  <p
                    className="m-text"
                    style={{ display: 'flex', justifyContent: 'center' }}
                  >
                    of{' '}
                    {groupOwnersCount > 0 ? (
                      groupOwnersCount
                    ) : (
                      <span>
                        <Skeleton
                          variant="rounded"
                          style={{ maxWidth: '3ch' }}
                          width={'3ch'}
                          height={20}
                          sx={{
                            margin: '0 3px',
                            bgcolor: 'var(--neutral-7)'
                          }}
                        />
                      </span>
                    )}{' '}
                    account owner
                    {groupOwnersCount > 1 ? 's' : ''}{' '}
                    {groupOwnersCount > 1 ? 'are' : 'is'} required to approve
                    all changes
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: mediumDevice ? "100%" : "inherit",
                    justifyContent: mediumDevice ? "flex-end" : "inherit",
                    gap: 'var(--space-s)',
                    padding:
                      mediumDevice ? 'var(--space-m) 0' : ''
                  }}
                >
                  <RouterLink
                    style={{ display: 'flex', cursor: 'pointer' }}
                    target="_blank"
                    rel="noopener noreferrer"
                    to={
                      'https://pool-faq.foundrydigital.com/what-is-the-request-approve-feature'
                    }
                  >
                    <CircleHelp
                      className="m-text"
                      size={0.8}
                      style={{ minWidth: 'var(--space-m)' }}
                    />
                  </RouterLink>

                  {
                    <>
                      <Button
                        variant="contained"
                        onClick={() => handleEditApprovalThreshold(groupName)}
                        disabled={approverThreshold > groupOwnersCount}
                      >
                        Save
                      </Button>

                      <Button
                        className={classes.redesignBtn}
                        variant="outlined"
                        onClick={() => {
                          setEditApproverThreshold(false);
                          setApproverThreshold(groupApprovalThreshold); // reset approverThreshold value to what is started as
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  }
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {approvals?.length > 0 
        ? mediumDevice 
          ? <Card sx={{backgroundColor: "var(--neutral-3)", marginLeft: "0", marginRight: "0", boxShadow: "none" }}>
              <Box sx={{marginTop: "8px"}}>
                <TableSortControls
                  selectorValues={
                      approvals.filter(i => i.subAccountName).length 
                        ? {
                            clear: t("clear"),
                            requestType: t('approvalType'),
                            subAccountName: t("subAccount"),
                            expiresAt: t('expiration'),
                          }
                        : {
                            clear: t("clear"),
                            requestType: t('approvalType'),
                            expiresAt: t('expiration'),
                          }
                  }
                  onChange={handleMobileUsersCardsSort}
                  defaultSort={'expiresAt'}
                  defaultOrder={'asc'}
                />
              </Box>
              {
                renderApprovalsCards()
              }
            </Card>
          : <Card style={{ backgroundColor: 'var(--neutral-2)' }}>
              <Box
                position={'relative'}
                paddingX={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '0'
                }}
                justifyContent={'center'}
              >
                <TableContainer component={Paper}>
                  <Table
                    className={classes.metalTable}
                    size="small"
                  >
                    <TableHead>
                      <TableRow
                        style={{
                          height: '40px',
                          backgroundColor: 'var(--color-surface-raised)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <TableCell
                          align="left"
                          onClick={
                            openRows.length > 0
                              ? () => setOpenRows([])
                              : () =>
                                  setOpenRows(
                                    approvals.map(
                                      approval => approval.approvalRequestId
                                    )
                                  )
                          }
                        >
                          <IconButton
                            size="small"
                            style={{
                              transform:
                                openRows.length > 0
                                  ? 'rotate(0deg)'
                                  : 'rotate(180deg)'
                            }}
                          >
                            <KeyboardArrowUpIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">
                          <TableSortLabel
                            active={orderBy === 'expiresAt'}
                            direction={order}
                            onClick={() => handleColumnSort('expiresAt')}
                          >
                            {t('expiration')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="left">
                          <TableSortLabel
                            active={orderBy === 'requestType'}
                            direction={order}
                            onClick={() => handleColumnSort('requestType')}
                          >
                            {t('approvalType')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="left">
                          <TableSortLabel
                            active={orderBy === 'subAccountName'}
                            direction={order}
                            onClick={() => handleColumnSort('subAccountName')}
                          >
                            {t('subAccount')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="left">
                          <TableSortLabel
                            active={orderBy === 'status'}
                            direction={order}
                            onClick={() => handleColumnSort('status')}
                          >
                            {t('status')}
                          </TableSortLabel>
                        </TableCell>

                      </TableRow>
                    </TableHead>
                    {renderApprovalsTable()}
                  </Table>
                </TableContainer>
              </Box>
            </Card> 
        : (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          { approvalsLoading ? <CircularProgress size={24} />  : <NoRequests /> }
        </div>
      )}
    </Card>
  );
};
export default ApprovalsTable;
